import { Clock, CheckCircle2, BookOpen, Headphones, Sunrise, Moon, Sun, CloudSun, Sunset, Sparkles, Target, CalendarPlus } from 'lucide-react';

const CATEGORY_CONFIG = {
  priority: { label_en: 'Priorities', label_hr: 'Prioriteti', Icon: Target, color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  prayer: { label_en: 'Prayers', label_hr: 'Namazi', Icon: Sunrise, color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'quran-reading': { label_en: 'Qur\'an Reading', label_hr: 'Citanje Kur\'ana', Icon: BookOpen, color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  'quran-listening': { label_en: 'Qur\'an Listening', label_hr: 'Slusanje Kur\'ana', Icon: Headphones, color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  dhikr: { label_en: 'Dhikr', label_hr: 'Zikr', Icon: Sparkles, color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  hifz: { label_en: 'Hifz', label_hr: 'Hifz', Icon: BookOpen, color: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500' },
  bereket: { label_en: 'Bereket Activities', label_hr: 'Bereket aktivnosti', Icon: Sparkles, color: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
  general: { label_en: 'General', label_hr: 'Opcenito', Icon: CalendarPlus, color: 'bg-stone-100 text-stone-600', dot: 'bg-stone-400' },
  quran: { label_en: 'Qur\'an', label_hr: 'Kur\'an', Icon: BookOpen, color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  sadaqah: { label_en: 'Sadaqah', label_hr: 'Sadaka', Icon: Sparkles, color: 'bg-pink-100 text-pink-700', dot: 'bg-pink-500' },
  learning: { label_en: 'Learning', label_hr: 'Ucenje', Icon: BookOpen, color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  other: { label_en: 'Other', label_hr: 'Ostalo', Icon: CalendarPlus, color: 'bg-stone-100 text-stone-600', dot: 'bg-stone-400' },
};

const getCategoryConfig = (category) => CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;

function GroupCard({ group, lang, side }) {
  const config = getCategoryConfig(group.category);
  const Icon = config.Icon;
  const label = lang === 'hr' ? config.label_hr : config.label_en;

  const showMinutes = group.total_minutes > 0;
  const showPoints = group.total_points > 0;

  // Build summary titles from individual items (first 3)
  const itemTitles = (group.items || []).slice(0, 3).map((item) => item.title);
  const hasMore = (group.items || []).length > 3;

  return (
    <div
      className="flex items-start gap-3 p-3 rounded-xl bg-white border border-stone-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
      data-testid={`pva-group-${side}-${group.category}`}
    >
      {/* Dot indicator */}
      <div className="flex-shrink-0 mt-1">
        <div className={`w-2.5 h-2.5 rounded-full ${config.dot}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${config.color}`}>
            <Icon className="w-3.5 h-3.5" />
            {label}
          </span>
          {group.count > 1 && (
            <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-stone-800 text-white text-[11px] font-bold" data-testid={`pva-count-${side}-${group.category}`}>
              {group.count}
            </span>
          )}
        </div>

        {/* Item titles */}
        <div className="mt-1.5 space-y-0.5">
          {itemTitles.map((title, i) => (
            <p key={i} className="text-[13px] text-stone-600 leading-snug truncate">{title}</p>
          ))}
          {hasMore && (
            <p className="text-[12px] text-stone-400 italic">+{(group.items || []).length - 3} {lang === 'hr' ? 'vise' : 'more'}</p>
          )}
        </div>

        {/* Stats row */}
        {(showMinutes || showPoints) && (
          <div className="flex items-center gap-3 mt-2">
            {showMinutes && (
              <span className="inline-flex items-center gap-1 text-[12px] text-stone-500 font-medium">
                <Clock className="w-3 h-3" /> {group.total_minutes} min
              </span>
            )}
            {showPoints && (
              <span className="inline-flex items-center gap-1 text-[12px] text-stone-500 font-medium">
                <Sparkles className="w-3 h-3" /> {group.total_points} {lang === 'hr' ? 'bod.' : 'pts'}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ColumnHeader({ title, count, accent }) {
  return (
    <div className="flex items-center justify-between mb-3 pb-2 border-b border-stone-100">
      <h4 className={`text-sm font-bold tracking-wide uppercase ${accent}`}>{title}</h4>
      <span className="text-xs text-stone-400 font-medium">{count} {count === 1 ? 'group' : 'groups'}</span>
    </div>
  );
}

export default function PlannedVsActualView({ timelineData, lang }) {
  const groupedPlanned = timelineData?.groupedPlanned || [];
  const groupedActual = timelineData?.groupedActual || [];

  const isEmpty = groupedPlanned.length === 0 && groupedActual.length === 0;

  return (
    <div className="p-4 md:p-6 min-h-[340px]" data-testid="planner-planned-vs-actual-view">
      {isEmpty ? (
        <div className="text-center py-16">
          <Clock className="w-12 h-12 text-stone-200 mx-auto mb-4" />
          <p className="text-stone-500 text-sm font-medium" data-testid="pva-empty-text">
            {lang === 'hr'
              ? 'Nema planova ni aktivnosti za ovaj dan.'
              : 'No plans or activities for this day.'}
          </p>
          <p className="text-stone-400 text-xs mt-1">
            {lang === 'hr'
              ? 'Dodaj prioritete ili logiraj aktivnosti da vidis usporedbu.'
              : 'Add priorities or log activities to see the comparison.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pva-columns">
          {/* PLANNED column */}
          <div data-testid="pva-planned-column">
            <ColumnHeader
              title={lang === 'hr' ? 'Planirano' : 'Planned'}
              count={groupedPlanned.length}
              accent="text-blue-700"
            />
            {groupedPlanned.length > 0 ? (
              <div className="space-y-2.5">
                {groupedPlanned.map((group) => (
                  <GroupCard key={group.category} group={group} lang={lang} side="planned" />
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-400 text-center py-6 italic" data-testid="pva-planned-empty">
                {lang === 'hr' ? 'Nema planova za ovaj dan.' : 'No plans for this day.'}
              </p>
            )}
          </div>

          {/* ACTUAL column */}
          <div data-testid="pva-actual-column">
            <ColumnHeader
              title={lang === 'hr' ? 'Stvarno' : 'Actual'}
              count={groupedActual.length}
              accent="text-emerald-700"
            />
            {groupedActual.length > 0 ? (
              <div className="space-y-2.5">
                {groupedActual.map((group) => (
                  <GroupCard key={group.category} group={group} lang={lang} side="actual" />
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-400 text-center py-6 italic" data-testid="pva-actual-empty">
                {lang === 'hr' ? 'Nema aktivnosti za ovaj dan.' : 'No activities for this day.'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
